import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import BasePageLayout from '../../components/BasePageLayout';
import { LongRunningLoadingState } from '../../components/common/LongRunningLoadingState';
import { 
  AILaunchAssetsService, 
  LaunchAssetOption, 
  LaunchAssetType,
  GeneratedLaunchAsset,
  LaunchAssetContext 
} from '../../services/aiLaunchAssetsService';
import { JourneyLocalStorageService } from '../../services/journeyLocalStorage';
import { showToast } from '../../utils/toast';
import '../../styles/pages/LaunchAssetsGeneratorPage.css';

const LaunchAssetsGeneratorPage: React.FC = () => {
  const { user } = useAuth();
  const { uiLanguage, t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const journeyId = searchParams.get('journeyId');
  const stepId = searchParams.get('stepId');
  const projectId = searchParams.get('projectId');

  // Get asset options with translations - updates when language changes
  const assetOptions = AILaunchAssetsService.getAssetOptions(t);
  
  const [selectedAssets, setSelectedAssets] = useState<Set<LaunchAssetType>>(new Set());
  const [projectContext, setProjectContext] = useState<LaunchAssetContext | null>(null);
  const [generatedAssets, setGeneratedAssets] = useState<GeneratedLaunchAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingContext, setLoadingContext] = useState(true);
  const [activeTab, setActiveTab] = useState<'select' | 'generated'>('select');
  const [expandedAsset, setExpandedAsset] = useState<string | null>(null);
  const [assetsFromStorage, setAssetsFromStorage] = useState(false);
  const [resolvedProjectId, setResolvedProjectId] = useState<string | null>(null);

  // Load project context and saved assets
  useEffect(() => {
    const loadContext = async () => {
      // Wait a bit to ensure user is fully loaded
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setLoadingContext(true);
      
      console.log('🔍 LaunchAssetsGeneratorPage: Loading context', {
        projectId,
        journeyId,
        userId: user?.id,
        userAuthenticated: !!user,
        userEmail: user?.email
      });
      
      let targetProjectId = projectId;
      
      // If no projectId in URL, fetch user's most recent pre-release project
      if (!targetProjectId && user?.id) {
        console.log('📍 No projectId in URL, fetching latest project for user:', user.id);
        targetProjectId = await AILaunchAssetsService.getLatestPreReleaseProject(user.id);
        console.log('📍 Found project ID:', targetProjectId);
        
        if (!targetProjectId) {
          setLoadingContext(false);
          console.log('❌ No project found, showing error to user');
          showToast(t('launchAssets.noProjectFound'), 'error');
          return;
        }
      } else if (!targetProjectId && !user?.id) {
        setLoadingContext(false);
        console.log('❌ No projectId and no authenticated user');
        showToast(t('launchAssets.mustBeLoggedIn'), 'error');
        return;
      }
      
      if (targetProjectId) {
        console.log('📍 Loading context for project:', targetProjectId);
        
        // Store the resolved project ID for later use
        setResolvedProjectId(targetProjectId);
        
        const context = await AILaunchAssetsService.getProjectContext(targetProjectId);
        console.log('📍 Project context loaded:', context);
        
        if (!context) {
          setLoadingContext(false);
          showToast(t('launchAssets.failedToLoad'), 'error');
          return;
        }
        
        if (journeyId) {
          const journeyContext = await AILaunchAssetsService.getJourneyContext(journeyId);
          if (journeyContext) {
            context.market = journeyContext.market;
            context.persona = journeyContext.persona;
          }
        }
        
        setProjectContext(context);
        
        // Check for saved assets in localStorage
        const savedAssets = JourneyLocalStorageService.getLaunchAssets(targetProjectId);
        if (savedAssets && savedAssets.length > 0) {
          console.log(`✅ Loaded ${savedAssets.length} saved assets from localStorage`);
          setGeneratedAssets(savedAssets);
          setAssetsFromStorage(true);
          setActiveTab('generated');
          showToast(t('launchAssets.previouslyGenerated') + `: ${savedAssets.length}`, 'info');
        }
        
        setLoadingContext(false);
        console.log('✅ Context loaded successfully');
      } else {
        setLoadingContext(false);
        showToast(t('launchAssets.noProjectFound'), 'error');
      }
    };

    loadContext();
  }, [projectId, journeyId, user?.id]);

  // Toggle asset selection
  const toggleAssetSelection = (assetType: LaunchAssetType) => {
    const newSelected = new Set(selectedAssets);
    if (newSelected.has(assetType)) {
      newSelected.delete(assetType);
    } else {
      newSelected.add(assetType);
    }
    setSelectedAssets(newSelected);
  };

  // Select all assets in a category
  const selectCategory = (category: string) => {
    const categoryAssets = assetOptions.filter(opt => opt.category === category);
    const newSelected = new Set(selectedAssets);
    
    categoryAssets.forEach(asset => {
      newSelected.add(asset.id);
    });
    
    setSelectedAssets(newSelected);
  };

  // Select all assets
  const selectAll = () => {
    const newSelected = new Set<LaunchAssetType>();
    assetOptions.forEach(opt => newSelected.add(opt.id));
    setSelectedAssets(newSelected);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedAssets(new Set());
  };

  // Generate selected assets
  const generateAssets = async () => {
    // Prevent regeneration if assets already exist
    if (generatedAssets.length > 0 && assetsFromStorage) {
      showToast(t('launchAssets.alreadyGenerated') + '. ' + t('launchAssets.useRegenerateButton'), 'warning');
      return;
    }

    if (selectedAssets.size === 0) {
      showToast(t('launchAssets.selectAtLeastOne'), 'warning');
      return;
    }

    if (!projectContext) {
      console.error('❌ Project context is null when trying to generate');
      console.log('Debug info:', {
        projectId,
        journeyId,
        userId: user?.id,
        projectContext
      });
      showToast(t('launchAssets.noProjectContext'), 'error');
      return;
    }

    // Check points before generating
    if (user?.id) {
      const { AIPointsService } = await import('../../services/aiPointsService');
      const requiredPoints = AIPointsService.getPointsCost('launchAssets');
      const pointsCheck = await AIPointsService.checkSufficientPoints(user.id, 'launchAssets');
      
      if (!pointsCheck.canAfford) {
        const errorMessage = t('launchAssets.insufficientPoints', {
          required: requiredPoints,
          current: pointsCheck.currentPoints,
          needed: pointsCheck.shortfall
        }) || `You need ${requiredPoints} points to generate launch assets. You currently have ${pointsCheck.currentPoints} points. Earn ${pointsCheck.shortfall} more points to unlock this feature.`;
        
        showToast(errorMessage, 'error');
        return;
      }
    }

    setLoading(true);
    
    try {
      console.log('🌐 Generating launch assets with system language:', uiLanguage);
      const assets = await AILaunchAssetsService.generateMultipleAssets(
        Array.from(selectedAssets),
        projectContext,
        uiLanguage,
        resolvedProjectId || projectId || undefined,
        t
      );
      
      // Deduct points after successful generation
      console.log('💰 Launch assets generated, checking points deduction...');
      if (user?.id) {
        console.log('💰 User authenticated, proceeding with points deduction');
        const { AIPointsService } = await import('../../services/aiPointsService');
        const projectIdForPoints = resolvedProjectId || projectId || journeyId || 'unknown';
        console.log('💰 Project ID for points:', projectIdForPoints);
        
        const deductResult = await AIPointsService.deductPoints(
          user.id,
          'launchAssets',
          projectIdForPoints
        );
        
        console.log('💰 Points deduction result:', deductResult);
        
        if (!deductResult.success) {
          console.error('❌ Failed to deduct points:', deductResult.error);
          // Don't fail the operation, just log the error
        } else {
          console.log('✅ Successfully deducted points for launch assets generation');
        }
      } else {
        console.warn('⚠️ User not authenticated, skipping points deduction');
      }
      
      setGeneratedAssets(assets);
      setAssetsFromStorage(false);
      
      // Save to localStorage using the resolved project ID
      const projectIdToSave = resolvedProjectId || projectId || undefined;
      console.log('💾 Saving assets to localStorage with projectId:', projectIdToSave);
      JourneyLocalStorageService.saveLaunchAssets(assets, projectIdToSave);
      
      setActiveTab('generated');
      showToast(t('launchAssets.generatedSuccessfully', { count: assets.length }), 'success');
    } catch (error) {
      console.error('Error generating assets:', error);
      showToast(t('launchAssets.failedToGenerate'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Regenerate assets (clear storage and allow new generation)
  const regenerateAssets = () => {
    JourneyLocalStorageService.clearLaunchAssets();
    setGeneratedAssets([]);
    setAssetsFromStorage(false);
    setSelectedAssets(new Set());
    setActiveTab('select');
    showToast(t('launchAssets.readyToGenerate'), 'info');
  };

  // Copy asset content to clipboard
  const copyToClipboard = (content: string) => {
    navigator.clipboard.writeText(content);
    showToast(t('launchAssets.copiedToClipboard'), 'success');
  };

  // Download asset as file
  const downloadAsset = (asset: GeneratedLaunchAsset) => {
    const content = typeof asset.content === 'string' 
      ? asset.content 
      : JSON.stringify(asset.content, null, 2);
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${asset.type}_${new Date().getTime()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showToast(t('launchAssets.assetDownloaded'), 'success');
  };

  // Download all assets as a single combined file
  const downloadAllAssets = () => {
    if (generatedAssets.length === 0) {
      showToast(t('launchAssets.noAssetsToDownload'), 'warning');
      return;
    }

    const timestamp = new Date().getTime();
    const projectName = projectContext?.projectTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'launch_assets';
    
    // Combine all assets into a single formatted document
    const combinedContent = generatedAssets.map((asset, index) => {
      const content = typeof asset.content === 'string' 
        ? asset.content 
        : JSON.stringify(asset.content, null, 2);
      
      const separator = '='.repeat(80);
      const header = `ASSET ${index + 1} of ${generatedAssets.length}: ${asset.title.toUpperCase()}`;
      const headerLine = '='.repeat(80);
      
      return `${headerLine}\n${header}\n${headerLine}\n\n${content}\n\n`;
    }).join('\n' + '='.repeat(80) + '\n\n');
    
    // Add header and footer
    const fileHeader = `LAUNCH ASSETS FOR: ${projectContext?.projectTitle || 'Your Project'}\n` +
                      `Generated: ${new Date().toLocaleString()}\n` +
                      `Total Assets: ${generatedAssets.length}\n` +
                      `${'='.repeat(80)}\n\n`;
    
    const fileFooter = `\n${'='.repeat(80)}\n` +
                      `END OF LAUNCH ASSETS\n` +
                      `Generated by SeedDots - ${new Date().toLocaleString()}`;
    
    const fullContent = fileHeader + combinedContent + fileFooter;
    
    // Create and download the combined file
    const blob = new Blob([fullContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName}_all_launch_assets_${timestamp}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showToast(t('launchAssets.downloadedAll', { count: generatedAssets.length }), 'success');
  };

  // Download assets as separate files (alternative method)
  const downloadAsSeparateFiles = async () => {
    if (generatedAssets.length === 0) {
      showToast(t('launchAssets.noAssetsToDownload'), 'warning');
      return;
    }

    const timestamp = new Date().getTime();
    const projectName = projectContext?.projectTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'launch_assets';
    
    showToast(t('launchAssets.preparingDownload', { count: generatedAssets.length }), 'info');
    
    // Use a longer delay and download sequentially to avoid browser blocking
    for (let i = 0; i < generatedAssets.length; i++) {
      const asset = generatedAssets[i];
      
      // Wait before each download
      await new Promise(resolve => setTimeout(resolve, i === 0 ? 0 : 500));
      
      const content = typeof asset.content === 'string' 
        ? asset.content 
        : JSON.stringify(asset.content, null, 2);
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName}_${asset.type}_${timestamp + i}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    
    showToast(t('launchAssets.downloadedSeparate', { count: generatedAssets.length }), 'success');
  };

  // Copy all assets to clipboard
  const copyAllAssets = () => {
    if (generatedAssets.length === 0) {
      showToast(t('launchAssets.noAssetsToCopy'), 'warning');
      return;
    }

    const allContent = generatedAssets.map(asset => {
      const content = typeof asset.content === 'string' 
        ? asset.content 
        : JSON.stringify(asset.content, null, 2);
      
      return `=== ${asset.title} ===\n\n${content}\n\n`;
    }).join('\n' + '='.repeat(80) + '\n\n');

    navigator.clipboard.writeText(allContent);
    showToast(t('launchAssets.copiedAll', { count: generatedAssets.length }), 'success');
  };

  // Group options by category
  const groupedOptions = assetOptions.reduce((acc, option) => {
    if (!acc[option.category]) {
      acc[option.category] = [];
    }
    acc[option.category].push(option);
    return acc;
  }, {} as Record<string, LaunchAssetOption[]>);

  const categoryInfo = {
    marketing: { title: t('launchAssets.categories.marketing'), icon: 'bi-megaphone-fill', color: '#3b82f6' },
    communications: { title: t('launchAssets.categories.communications'), icon: 'bi-chat-dots-fill', color: '#8b5cf6' },
    operations: { title: t('launchAssets.categories.operations'), icon: 'bi-gear-fill', color: '#10b981' },
    content: { title: t('launchAssets.categories.content'), icon: 'bi-file-text-fill', color: '#f59e0b' }
  };

  if (!user) {
    return (
      <BasePageLayout background="white" padding="md" maxWidth="lg">
        <div className="auth-required">
          <h2>{t('launchAssets.authRequired')}</h2>
          <p>{t('launchAssets.authRequiredMessage')}</p>
        </div>
      </BasePageLayout>
    );
  }

  if (loadingContext) {
    return (
      <BasePageLayout background="white" padding="md" maxWidth="xl">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
          <p>{t('launchAssets.loadingContext')}</p>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <>
      <LongRunningLoadingState 
        operation="launch-assets"
        isLoading={loading}
      />
      
      <BasePageLayout background="white" padding="md" maxWidth="xl">
        <div className="launch-assets-page">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <h1>
              <i className="bi bi-stars"></i>
              {t('launchAssets.pageTitle')}
            </h1>
            <p className="subtitle">
              {t('launchAssets.pageSubtitle')}
            </p>
            {projectContext && (
              <div className="project-info">
                <strong>{t('launchAssets.project')}:</strong> {projectContext.projectTitle}
              </div>
            )}
            {!projectContext && (
              <div className="project-info" style={{ background: '#fee2e2', color: '#dc2626' }}>
                <strong>⚠️ {t('launchAssets.noProjectContext')}</strong>
                <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Debug: userId={user?.id}, projectId={projectId || 'none'}, journeyId={journeyId || 'none'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <button
            className={`tab ${activeTab === 'select' ? 'active' : ''}`}
            onClick={() => setActiveTab('select')}
          >
            <i className="bi bi-ui-checks"></i>
            {t('launchAssets.selectTab')} ({selectedAssets.size})
          </button>
          <button
            className={`tab ${activeTab === 'generated' ? 'active' : ''}`}
            onClick={() => setActiveTab('generated')}
            disabled={generatedAssets.length === 0}
          >
            <i className="bi bi-file-earmark-check"></i>
            {t('launchAssets.generatedTab')} ({generatedAssets.length})
          </button>
        </div>

        {/* Selection Tab */}
        {activeTab === 'select' && (
          <div className="selection-container">
            {/* Toolbar */}
            <div className="toolbar">
              <div className="toolbar-actions">
                <button onClick={selectAll} className="btn btn-sm btn-outline">
                  <i className="bi bi-check-all"></i>
                  {t('launchAssets.selectAll')}
                </button>
                <button onClick={clearSelection} className="btn btn-sm btn-outline">
                  <i className="bi bi-x-circle"></i>
                  {t('launchAssets.clear')}
                </button>
              </div>
              <div className="selected-count">
                {t('launchAssets.selectedCount', { count: selectedAssets.size })}
              </div>
            </div>

            {/* Asset Categories */}
            {Object.entries(groupedOptions).map(([category, options]) => {
              const info = categoryInfo[category as keyof typeof categoryInfo];
              const allCategorySelected = options.every(opt => selectedAssets.has(opt.id));
              
              return (
                <div key={category} className="asset-category">
                  <div className="category-header">
                    <div className="category-title">
                      <i className={info.icon} style={{ color: info.color }}></i>
                      <h3>{info.title}</h3>
                      <span className="badge">{options.length}</span>
                    </div>
                    <button
                      onClick={() => selectCategory(category)}
                      className="btn btn-sm btn-link"
                    >
                      {allCategorySelected ? t('launchAssets.deselectAll') : t('launchAssets.selectAllInCategory')}
                    </button>
                  </div>
                  
                  <div className="asset-grid">
                    {options.map(option => {
                      const isSelected = selectedAssets.has(option.id);
                      
                      return (
                        <div
                          key={option.id}
                          className={`asset-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleAssetSelection(option.id)}
                        >
                          <div className="asset-card-header">
                            <div className="asset-icon">
                              <i className={option.icon}></i>
                            </div>
                            <div className="asset-checkbox">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          
                          <div className="asset-card-body">
                            <h4>{option.title}</h4>
                            <p>{option.description}</p>
                            
                            <div className="asset-meta">
                              {option.aiPowered && (
                                <span className="badge badge-ai">
                                  <i className="bi bi-stars"></i>
                                  {t('launchAssets.aiPowered')}
                                </span>
                              )}
                              <span className="badge badge-time">
                                <i className="bi bi-clock"></i>
                                {option.estimatedTime}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Generate Button */}
            <div className="generate-section">
              {assetsFromStorage && (
                <div className="storage-notice">
                  <i className="bi bi-check-circle-fill text-success"></i>
                  <span>{t('launchAssets.alreadyGenerated')}</span>
                  <p className="help-text" style={{ marginTop: '0.5rem' }}>
                    {t('launchAssets.viewGeneratedTab')}
                  </p>
                </div>
              )}
              <button
                onClick={generateAssets}
                disabled={selectedAssets.size === 0 || loading || (generatedAssets.length > 0 && assetsFromStorage)}
                className="btn btn-primary btn-lg"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {t('launchAssets.generatingAssets', { count: selectedAssets.size })}
                  </>
                ) : (
                  <>
                    <i className="bi bi-stars"></i>
                    {(generatedAssets.length > 0 && assetsFromStorage) 
                      ? t('launchAssets.alreadyGenerated')
                      : `${t('launchAssets.generate')} ${selectedAssets.size} ${selectedAssets.size !== 1 ? t('launchAssets.assets') : t('launchAssets.asset')}`
                    }
                  </>
                )}
              </button>
              {selectedAssets.size === 0 && !assetsFromStorage && (
                <p className="help-text">{t('launchAssets.selectAtLeastOne')}</p>
              )}
              {(generatedAssets.length > 0 && assetsFromStorage) && (
                <p className="help-text" style={{ color: '#059669' }}>
                  ✓ {t('launchAssets.assetsAlreadyExist')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Generated Assets Tab */}
        {activeTab === 'generated' && (
          <div className="generated-container">
            {generatedAssets.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-inbox"></i>
                <h3>{t('launchAssets.emptyStateTitle')}</h3>
                <p>{t('launchAssets.emptyStateMessage')}</p>
                <button onClick={() => setActiveTab('select')} className="btn btn-primary">
                  {t('launchAssets.selectAssetsButton')}
                </button>
              </div>
            ) : (
              <>
                {/* Info banner when assets are from storage */}
                {assetsFromStorage && (
                  <div className="info-banner">
                    <div className="info-content">
                      <i className="bi bi-info-circle-fill"></i>
                      <div>
                        <strong>{t('launchAssets.previouslyGenerated')}</strong>
                        <p>{t('launchAssets.previouslyGeneratedMessage')}</p>
                      </div>
                    </div>
                    <button onClick={regenerateAssets} className="btn btn-sm btn-outline">
                      <i className="bi bi-arrow-clockwise"></i>
                      {t('launchAssets.regenerateNew')}
                    </button>
                  </div>
                )}

                {/* Bulk Actions Toolbar */}
                <div className="bulk-actions-toolbar">
                  <div className="toolbar-info">
                    <i className="bi bi-file-earmark-check-fill text-success"></i>
                    <span>{t('launchAssets.bulkActionsInfo', { count: generatedAssets.length })}</span>
                  </div>
                  <div className="toolbar-actions">
                    <button 
                      onClick={copyAllAssets} 
                      className="btn btn-sm btn-outline"
                      title={t('launchAssets.copyAll')}
                    >
                      <i className="bi bi-clipboard"></i>
                      {t('launchAssets.copyAll')}
                    </button>
                    <div className="download-button-group">
                      <button 
                        onClick={downloadAllAssets} 
                        className="btn btn-sm btn-primary"
                        title={t('launchAssets.downloadAll')}
                      >
                        <i className="bi bi-download"></i>
                        {t('launchAssets.downloadAll')}
                      </button>
                      <button 
                        onClick={downloadAsSeparateFiles} 
                        className="btn btn-sm btn-outline"
                        title={t('launchAssets.downloadSeparate')}
                      >
                        <i className="bi bi-files"></i>
                        {t('launchAssets.downloadSeparate')}
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="assets-list">
                {generatedAssets.map((asset, index) => {
                  const isExpanded = expandedAsset === asset.type;
                  const contentString = typeof asset.content === 'string' 
                    ? asset.content 
                    : JSON.stringify(asset.content, null, 2);
                  
                  return (
                    <div key={index} className="generated-asset">
                      <div className="asset-header" onClick={() => setExpandedAsset(isExpanded ? null : asset.type)}>
                        <div className="asset-title">
                          <i className="bi bi-file-earmark-check-fill text-success"></i>
                          <h4>{asset.title}</h4>
                        </div>
                        <div className="asset-actions">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(contentString);
                            }}
                            className="btn btn-sm btn-outline"
                            title={t('launchAssets.copyToClipboard')}
                          >
                            <i className="bi bi-clipboard"></i>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadAsset(asset);
                            }}
                            className="btn btn-sm btn-outline"
                            title={t('launchAssets.downloadAsset')}
                          >
                            <i className="bi bi-download"></i>
                          </button>
                          <button className="btn btn-sm btn-link">
                            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                          </button>
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="asset-content">
                          <pre>{contentString}</pre>
                        </div>
                      )}
                      
                      {asset.metadata && (
                        <div className="asset-metadata">
                          <span>
                            <i className="bi bi-file-text"></i>
                            {asset.metadata.wordCount} {t('launchAssets.words')}
                          </span>
                          <span>
                            <i className="bi bi-text-paragraph"></i>
                            {asset.metadata.characterCount} {t('launchAssets.characters')}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </>
            )}
          </div>
        )}
        </div>
      </BasePageLayout>
    </>
  );
};

export default LaunchAssetsGeneratorPage;
